package com.sddamico.swigmore.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.components.layouts.Column
import com.sddamico.swigmore.components.layouts.Columns
import com.sddamico.swigmore.components.layouts.ContentColumn
import com.sddamico.swigmore.components.layouts.SignedInPage
import com.sddamico.swigmore.ds.Reveal
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.core.Eyebrow
import com.sddamico.swigmore.ds.core.SectionHeader
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.feedback.Countdown
import com.sddamico.swigmore.ds.feedback.PageTitleSkeleton
import com.sddamico.swigmore.ds.feedback.Skeleton
import com.sddamico.swigmore.ds.feedback.SkeletonBlock
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.poll.MatchupCard
import com.sddamico.swigmore.ds.poll.MatchupSide
import com.sddamico.swigmore.ds.poll.Movement
import com.sddamico.swigmore.ds.poll.RankCard
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeCaps
import com.sddamico.swigmore.ds.typeDisplay
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.ds.typeTeam
import com.sddamico.swigmore.models.CurrentPollResponse
import com.sddamico.swigmore.models.LeagueMatchup
import com.sddamico.swigmore.models.LeagueResponse
import com.sddamico.swigmore.models.MatchupScore
import com.sddamico.swigmore.models.MatchupTeam
import com.sddamico.swigmore.models.MatchupsResponse
import com.sddamico.swigmore.models.PollResultsResponse
import com.sddamico.swigmore.models.PollRules
import com.sddamico.swigmore.models.PollStanding
import com.sddamico.swigmore.models.TeamTallyDto
import com.sddamico.swigmore.models.WeekStatus
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.CachedResource
import com.sddamico.swigmore.util.HomeData
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.NOT_FINAL
import com.sddamico.swigmore.util.deadlineTime
import com.sddamico.swigmore.util.firstPlaceVotes
import com.sddamico.swigmore.util.fixed
import com.sddamico.swigmore.util.ordinal
import com.sddamico.swigmore.util.rememberCachedResource
import com.sddamico.swigmore.util.rememberNow
import com.sddamico.swigmore.util.shortDate
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Poll home: the latest published poll, this week's ballot, and Saturday's slate. */
@Page
@Composable
fun HomePage() {
  SignedInPage("", activeTab = AppTab.Poll) { signedIn ->
    val home = rememberCachedResource(CachedResource.Home).state
    val league = signedIn.league.state
    ContentColumn {
      when {
        home is LoadState.Failed -> Reveal { Hero("Swigmore coaches poll", "The poll is offline.", listOf(home.message)) }
        // Waits for the league too, so the standings and slate don't pop in after the rest.
        home is LoadState.Ready && league !is LoadState.Loading ->
          Reveal { HomeContent(home.value, (league as? LoadState.Ready)?.value) }
        else -> HomeSkeleton()
      }
    }
  }
}

@Composable
private fun HomeContent(home: HomeData, league: LeagueResponse?) {
  val results = home.results
  val recap = home.current.recap
  Columns(gap = "24px", modifier = Modifier.css("align-items" to "stretch", "margin-bottom" to "28px")) {
    Column("520px") {
      when {
        // A poll's write-up is stale once its week's games are played; the server sends their recap
        // until the next poll publishes.
        recap != null -> Hero(
          "Week ${recap.gamesWeek} in review · written by Claude from the Fantrax scores",
          recap.headline,
          recap.paragraphs,
        )
        results != null -> {
          val w = results.week
          val released = shortDate(checkNotNull(w.publishedAt))
          Hero(
            "Week ${w.week} poll · released $released · ${results.ballotCount} of ${results.memberCount} ballots",
            results.announcement?.headline ?: "The Week ${w.week} Coaches Poll",
            results.announcement?.paragraphs.orEmpty(),
          )
        }
        else -> Hero(
          "Swigmore coaches poll",
          "The first poll is on its way.",
          listOf("Every owner ranks the top eight once the first ballot opens. Until then, here's how the standings shake out."),
        )
      }
    }
    BallotCard(home.current, Modifier.css("flex" to "0 1 340px", "min-width" to "280px"))
  }
  Columns {
    Column("560px") {
      if (results != null) Rankings(results) else league?.let { Standings(it) }
    }
    Column("360px") { home.slate?.let { Slate(it, results, league) } }
  }
}

@Composable
private fun Hero(eyebrow: String, title: String, lede: List<String>) {
  Eyebrow(eyebrow)
  Div(attrs = Modifier.typeDisplay("clamp(32px,5vw,54px)").css("margin" to "12px 0 14px", "text-wrap" to "balance").toAttrs()) {
    Text(title)
  }
  lede.forEach { paragraph ->
    Div(attrs = Modifier.typeBody("16px").css("color" to SwColor.textSecondary, "max-width" to "620px",
      "text-wrap" to "pretty", "margin-bottom" to "10px").toAttrs()) { Text(paragraph) }
  }
}

@Composable
private fun BallotCard(current: CurrentPollResponse, modifier: Modifier) {
  val ctx = rememberPageContext()
  val now = rememberNow()
  val week = current.week
  val mine = current.myBallot
  // The status is from page load; the deadline can pass while the page is open.
  val open = week != null && week.status == WeekStatus.OPEN && week.closesAt > now
  Card(modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "12px"), padding = "18px", stitchInset = "6px") {
    Div(attrs = Modifier.css("display" to "flex", "justify-content" to "space-between", "align-items" to "baseline").toAttrs()) {
      Span(attrs = Modifier.typeCaps("10px", ".18em").toAttrs()) { Text(if (week == null) "Ballot" else "Week ${week.week} ballot") }
      Span(attrs = Modifier.typeMono("10px", 600).css("opacity" to ".7", "text-transform" to "uppercase").toAttrs()) {
        Text(when {
          week == null -> "Not open"
          !open -> "Locked"
          mine != null -> "● Submitted"
          else -> "○ Not yet voted"
        })
      }
    }
    if (open) Countdown(week.closesAt - now)
    Div(attrs = Modifier.typeBody("12px", 500, "1.4").css("opacity" to ".75").toAttrs()) {
      Text(when {
        week == null -> "The commissioner hasn't opened a ballot yet."
        open -> "Closes ${deadlineTime(week.closesAt)}. Amend or withdraw any time before then."
        week.status != WeekStatus.PUBLISHED -> "Ballots are locked. The Week ${week.week} poll is being tallied."
        else -> "Week ${week.week} is published. The next ballot opens after this week's games."
      })
    }
    if (week != null && current.canVote && (open || mine != null)) {
      val label = when {
        !open -> "View My Ballot"
        mine == null -> "Cast Your Ballot"
        else -> "Amend My Ballot"
      }
      Button(label, fullWidth = true, onClick = { ctx.router.navigateTo(AppTab.Ballot.path) })
    }
  }
}

@Composable
private fun Rankings(results: PollResultsResponse) {
  var open by remember { mutableStateOf<String?>(null) }
  val released = shortDate(checkNotNull(results.week.publishedAt))
  SectionHeader(
    "Week ${results.week.week} Top Eight",
    caption = "Released $released · ${results.ballotCount} of ${results.memberCount} ballots · " +
      "${PollRules.BALLOT_SIZE}–1 scoring · max ${results.ballotCount * PollRules.BALLOT_SIZE} · tap a team for the breakdown",
  )
  Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "8px").toAttrs()) {
    results.ranked.forEach { t ->
      val fpv = t.firstPlaceVotes
      RankCard(
        rank = t.rank,
        team = t.team.name,
        meta = listOfNotNull(t.team.record?.toString(), if (fpv > 0) firstPlaceVotes(fpv) else null)
          .joinToString(" · "),
        points = t.points,
        movement = Movement.of(t.rank, (t.previousStanding as? PollStanding.Ranked)?.rank),
        expanded = open == t.team.id,
        distribution = t.votesByRank,
        stats = listOfNotNull(
          "On ${t.onBallots}/${results.ballotCount} ballots",
          "Avg rank ${fixed(t.avgRank, 2)}",
          "High ${ordinal(t.highRank)} · Low ${ordinal(t.lowRank)}",
          if (results.firstPoll) null else "LW ${lastWeek(t)}",
        ),
        onToggle = { open = if (open == t.team.id) null else t.team.id },
      )
    }
  }
  if (results.alsoReceiving.isNotEmpty()) {
    Div(attrs = Modifier.typeBody("14px", lineHeight = "1.5").css("margin-top" to "16px", "color" to SwColor.textBody).toAttrs()) {
      Span(attrs = Modifier.typeCaps("10px", ".18em").css("color" to SwColor.gold, "margin-right" to "8px").toAttrs()) {
        Text("Also receiving votes")
      }
      Text(results.alsoReceiving.joinToString(", ", postfix = ".") { "${it.team.name} ${it.points}" })
    }
  }
}

private fun lastWeek(t: TeamTallyDto): String = when (val s = t.previousStanding) {
  is PollStanding.Ranked -> "#${s.rank}"
  PollStanding.ReceivingVotes -> "RV"
  PollStanding.Unranked -> "NR"
}

/** Before the first poll: the Fantrax standings, so the early weeks still show who's good. */
@Composable
private fun Standings(league: LeagueResponse) {
  val names = league.teams.associateBy { it.id }
  SectionHeader("Standings", caption = league.standingsWeek?.let { "Going into week $it · via Fantrax" })
  Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "8px").toAttrs()) {
    league.standings.sortedBy { it.standingsRank }.take(8).forEach { s ->
      Card(padding = "12px 14px", radius = SwEffects.radiusCard, stitchInset = "5px") {
        Div(attrs = Modifier.css("display" to "grid", "grid-template-columns" to "44px minmax(0,1fr) auto",
          "align-items" to "center", "gap" to "12px").toAttrs()) {
          Div(attrs = Modifier.typeDisplay("32px").css("text-align" to "center").toAttrs()) { Text(s.standingsRank.toString()) }
          Div(attrs = Modifier.typeTeam("16px").css("min-width" to "0").toAttrs()) { Text(names[s.teamId]?.name ?: s.teamId) }
          Div(attrs = Modifier.css("text-align" to "right").toAttrs()) {
            Div(attrs = Modifier.typeDisplay("20px").toAttrs()) { Text(s.record.toString()) }
            Div(attrs = Modifier.typeMono("10px", 600).css("margin-top" to "4px", "opacity" to ".75").toAttrs()) {
              Text(s.pointsPerGame?.let { "${fixed(it, 1)} PPG" } ?: "")
            }
          }
        }
      }
    }
  }
}

/**
 * Games involving a team with votes, ranked-vs-ranked first and then by the better rank. Before the
 * first poll, every game, by combined Fantrax standing.
 */
@Composable
private fun Slate(slate: MatchupsResponse, results: PollResultsResponse?, league: LeagueResponse?) {
  val standings = results?.let { (it.ranked + it.alsoReceiving).associate { t -> t.team.id to PollStanding.of(t.rank) } }.orEmpty()
  val standing: (String) -> PollStanding = { id -> standings[id] ?: PollStanding.Unranked }
  val games = if (results == null) {
    val rank = league?.standings.orEmpty().associate { it.teamId to it.standingsRank }
    slate.matchups.sortedBy { (rank[it.home.teamId] ?: 99) + (rank[it.away.teamId] ?: 99) }
  } else {
    slate.matchups
      .filter { standing(it.home.teamId) != PollStanding.Unranked || standing(it.away.teamId) != PollStanding.Unranked }
      .sortedWith(compareBy({ -rankedSides(it, standing) }, { bestRank(it, standing) }))
  }
  SectionHeader("Saturday's Slate", caption = slate.week?.let { "Week $it · via Fantrax" })
  if (games.isEmpty()) {
    Div(attrs = Modifier.typeBody("14px").css("color" to SwColor.textMuted).toAttrs()) { Text("No games on the schedule yet.") }
    return
  }
  Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "10px").toAttrs()) {
    games.forEachIndexed { i, g ->
      val home = standing(g.home.teamId)
      val away = standing(g.away.teamId)
      val tag = when { i == 0 -> "Game of the week"; results == null -> ""; else -> tagFor(home, away) }
      MatchupCard(
        tag = tag,
        note = when (g.score?.final) { null -> if (tag.isEmpty()) "" else "Sat"; true -> "Final"; false -> NOT_FINAL },
        home = side(g.home, home, g.score?.home, g.score),
        away = side(g.away, away, g.score?.away, g.score),
      )
    }
  }
  Div(attrs = Modifier.typeBody("12px", lineHeight = "1.5").css("margin-top" to "10px", "color" to SwColor.textMuted).toAttrs()) {
    val readout = if (games.any { it.score != null }) "Scores via Fantrax." else "Season points per game."
    Text(
      if (results == null) "Every game this week, best combined standing first. $readout"
      else "Every game this week with a team receiving votes. Ranked-vs-ranked first. $readout"
    )
  }
}

private fun rankedSides(g: LeagueMatchup, standing: (String) -> PollStanding) =
  listOf(g.home, g.away).count { standing(it.teamId) is PollStanding.Ranked }

private fun bestRank(g: LeagueMatchup, standing: (String) -> PollStanding) =
  listOf(g.home, g.away).minOf { (standing(it.teamId) as? PollStanding.Ranked)?.rank ?: (PollRules.BALLOT_SIZE + 1) }

private fun tagFor(a: PollStanding, b: PollStanding): String {
  val ranked = listOf(a, b).count { it is PollStanding.Ranked }
  val rv = listOf(a, b).count { it == PollStanding.ReceivingVotes }
  return when {
    ranked == 2 -> "Ranked vs Ranked"
    ranked == 1 && rv == 1 -> "Ranked vs RV"
    ranked == 1 -> "Ranked in action"
    rv == 2 -> "RV vs RV"
    else -> "Receiving votes"
  }
}

private fun side(team: MatchupTeam, standing: PollStanding, points: Double?, score: MatchupScore?) =
  MatchupSide(
    team.name, team.record?.toString() ?: "", standing,
    readout = points?.let { fixed(it, 1) } ?: team.pointsPerGame?.let { "${fixed(it, 1)} PPG" },
    winner = score?.winnerTeamId == team.teamId,
  )

@Composable
private fun HomeSkeleton() {
  Skeleton {
    Columns(gap = "24px", modifier = Modifier.css("align-items" to "stretch", "margin-bottom" to "28px")) {
      Column("520px") { PageTitleSkeleton() }
      SkeletonBlock("190px", Modifier.css("flex" to "0 1 340px", "min-width" to "280px"))
    }
    Columns {
      Column("560px") { SkeletonList(rows = PollRules.BALLOT_SIZE, rowHeight = "62px") }
      Column("360px") { SkeletonList(rows = 3, rowHeight = "112px") }
    }
  }
}

@Composable
private fun SkeletonList(rows: Int, rowHeight: String) {
  SkeletonBlock("22px", Modifier.css("margin-bottom" to "14px"), width = "180px", radius = SwEffects.radiusXs)
  Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "8px").toAttrs()) {
    repeat(rows) { SkeletonBlock(rowHeight) }
  }
}
